/**
 * Firebase token helper with timeout protection.
 * Prevents app from hanging when Firebase token refresh fails on bad network.
 *
 * Background: getIdToken(true) can hang indefinitely on Android when offline.
 * Solution: Use getIdToken(false) with timeout wrapper.
 *
 * @see https://github.com/firebase/firebase-js-sdk/issues/8490
 * @see https://github.com/invertase/react-native-firebase/issues/8394
 */

import { message } from 'antd';
import { reportFirebaseTokenError } from './sentryHelpers';

const DEFAULT_TIMEOUT_MS = 5000;
const FORCE_REFRESH_TIMEOUT_MS = 15000;
const MAX_RETRIES = 1;

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

// Share a single in-flight forced refresh across concurrent callers. The
// Firebase SDK does not dedupe forced refreshes, so several upload/auth calls
// firing at once could each trigger a network refresh and hit auth/quota-exceeded.
// Keyed by uid so a refresh in flight for one user is never handed to another.
let inflightForceRefresh = null;
let inflightForceRefreshUid = null;
const forceRefreshOnce = (currentUser) => {
  const uid = currentUser?.uid || null;
  if (!inflightForceRefresh || inflightForceRefreshUid !== uid) {
    inflightForceRefreshUid = uid;
    inflightForceRefresh = getTokenWithTimeout(
      currentUser,
      FORCE_REFRESH_TIMEOUT_MS,
      true,
    ).finally(() => {
      inflightForceRefresh = null;
      inflightForceRefreshUid = null;
    });
  }
  return inflightForceRefresh;
};

/**
 * Get Firebase ID token with timeout protection.
 * getIdToken(false) returns the cached token and lets the SDK refresh it when
 * expired; getIdToken(true) forces a network refresh. Both can hang on a weak
 * network, so we cap them with a timeout.
 *
 * @param {firebase.User} currentUser - Firebase user object
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @param {boolean} forceRefresh - Force a network refresh of the token
 * @returns {Promise<string>} - ID token or throws on timeout/error
 */
export const getTokenWithTimeout = (
  currentUser,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  forceRefresh = false,
) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(
          forceRefresh
            ? 'Firebase token refresh timeout'
            : 'Firebase token timeout',
        ),
      );
    }, timeoutMs);

    currentUser
      .getIdToken(forceRefresh)
      .then((token) => {
        clearTimeout(timeoutId);
        resolve(token);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        reject(err);
      });
  });
};

/**
 * Safely get Firebase ID token with retry and user feedback.
 * Retries once on failure, shows message if still failing.
 *
 * @param {firebase.User|null} currentUser - Firebase user object or null
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @param {boolean} showMessage - Show user message on failure (default: true)
 * @returns {Promise<string>} - ID token or empty string
 */
export const getSafeToken = async (
  currentUser,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  showMessage = true,
) => {
  if (!currentUser) {
    return '';
  }

  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await getTokenWithTimeout(currentUser, timeoutMs);
    } catch (error) {
      lastError = error;
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }
  }

  // Cached path exhausted (usually an expired token whose refresh was too slow).
  // Force a real network refresh with a longer timeout before giving up, so the
  // caller gets a valid token instead of proceeding with none. Skip it when
  // offline — a network refresh can't succeed and would only stall for 15s.
  let forcedRefreshAttempted = false;
  if (!isOffline()) {
    forcedRefreshAttempted = true;
    try {
      return await forceRefreshOnce(currentUser);
    } catch (error) {
      lastError = error;
    }
  }

  console.warn('[Firebase] Token fetch failed after retries:', lastError?.message);
  reportFirebaseTokenError(lastError, {
    cachedTimeoutMs: timeoutMs,
    cachedAttempts: MAX_RETRIES + 1,
    forceRefreshTimeoutMs: FORCE_REFRESH_TIMEOUT_MS,
    forcedRefreshAttempted,
    offline: isOffline(),
    source: 'firebaseTokenHelper',
  });

  if (showMessage) {
    message.warning(
      'Sesija istekla. Pokušajte se odjaviti i ponovo prijaviti.',
      5,
    );
  }

  return '';
};

export default getTokenWithTimeout;
